import { Fragment, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AnimatePresence, motion } from 'framer-motion';
import type { MoviefoneConversationSummary } from 'gen/stream-api/requests/types.gen';
import { parseConversationTimestamp } from 'util/moviefone';

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 40;

interface MoviefonePreviousThreadsProps {
  conversations: MoviefoneConversationSummary[];
  isLoading: boolean;
  /** A new conversation's first message has been sent but hasn't finished saving yet. */
  isPendingNewConversation: boolean;
  activeTimestamp: string | null;
  /** Conversations with something new since the user last looked - shown with a dot,
   * cleared once opened. */
  unreadTimestamps: Set<string>;
  onSelectConversation: (timestamp: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (timestamp: string) => void;
}

const MoviefonePreviousThreads = ({
  conversations,
  isLoading,
  isPendingNewConversation,
  activeTimestamp,
  unreadTimestamps,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
}: MoviefonePreviousThreadsProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [menuState, setMenuState] = useState<{
    anchor: HTMLElement;
    conversation: MoviefoneConversationSummary;
  } | null>(null);
  const [conversationPendingDelete, setConversationPendingDelete] =
    useState<MoviefoneConversationSummary | null>(null);

  return (
    <Fragment>
      <Stack
        sx={{
          width: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          minWidth: open ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          height: '100%',
          overflow: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: open
            ? theme.palette.background.paper
            : 'transparent',
          borderRadius: open ? 2 : 0,
          transition: 'width 0.15s ease, min-width 0.15s ease',
        }}
      >
        {open && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 0.5,
              px: 2,
              py: 2,
            }}
          >
            {/* TODO: link to a support doc once one exists. */}
            <Tooltip title="About" placement="right">
              <IconButton
                data-ph-capture-attribute-element="moviefone-threads-info"
                size="small"
                sx={{ color: theme.palette.sgGreen }}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Minimize" placement="right">
              <IconButton
                data-ph-capture-attribute-element="moviefone-threads-minimize"
                size="small"
                onClick={() => setOpen(false)}
                sx={{ color: theme.palette.sgGreen }}
              >
                <UnfoldLessIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {open ? (
          <Stack sx={{ flex: 1, overflowY: 'auto', px: 4 }}>
            <Stack
              direction="row"
              onClick={onNewConversation}
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                mb: 1.5,
                px: 1,
                py: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, 0.04),
                },
              }}
            >
              <AddIcon
                fontSize="small"
                sx={{ color: theme.palette.text.secondary }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: '13px', color: theme.palette.text.secondary }}
              >
                New query
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1,
                mt: 3,
                mb: 3,
                color: theme.palette.text.primary,
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
              }}
            >
              Recent
            </Typography>
            {isLoading && (
              <Stack sx={{ alignItems: 'center', py: 2, mb: 1 }}>
                <CircularProgress size={18} />
              </Stack>
            )}
            {isPendingNewConversation && (
              <Box sx={{ pl: 4, pr: 2, py: 1, mb: 1 }}>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton
                  variant="text"
                  width="40%"
                  height={12}
                  sx={{ mt: 0.25 }}
                />
              </Box>
            )}
            <AnimatePresence initial={false} mode="popLayout">
              {!isLoading &&
                !isPendingNewConversation &&
                conversations.length === 0 && (
                  <motion.div
                    key="empty-state"
                    // motion.div doesn't understand the sx prop, so its own sizing needs a
                    // plain style - without it, it shrink-wraps instead of filling the
                    // sidebar and the Stack below can't center within it.
                    style={{ display: 'flex', flex: 1 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                  >
                    <Stack
                      sx={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        py: 2,
                      }}
                    >
                      <ForumOutlinedIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '12px',
                          fontStyle: 'italic',
                        }}
                      >
                        No previous conversations yet
                      </Typography>
                    </Stack>
                  </motion.div>
                )}
              {!isLoading &&
                conversations.map((conversation) => (
                  <motion.div
                    key={conversation.timestamp}
                    layout
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                  >
                    <Stack
                      direction="row"
                      onClick={() =>
                        onSelectConversation(conversation.timestamp)
                      }
                      sx={{
                        cursor: 'pointer',
                        alignItems: 'center',
                        borderRadius: 1,
                        pl: 4,
                        pr: 2,
                        py: 2,
                        mb: 1,
                        backgroundColor:
                          conversation.timestamp === activeTimestamp
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(
                            theme.palette.text.primary,
                            0.04,
                          ),
                        },
                        '&:hover .conversation-delete-button': {
                          opacity: 1,
                        },
                        '&:hover .conversation-unread-dot': {
                          opacity: 0,
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontSize: '12px',
                            fontWeight:
                              conversation.timestamp === activeTimestamp
                                ? 600
                                : 400,
                            color: theme.palette.text.primary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {conversation.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.25,
                            fontSize: '9px',
                            fontStyle: 'italic',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {parseConversationTimestamp(
                            conversation.last_interacted,
                          ).format('MMM D, h:mm A')}
                        </Typography>
                      </Box>
                      {/* The unread dot and the kebab share this same slot - hovering the row
                          crossfades from one to the other, matching how ChatGPT does it. */}
                      <Box
                        sx={{
                          position: 'relative',
                          flexShrink: 0,
                          width: 28,
                          height: 28,
                        }}
                      >
                        {unreadTimestamps.has(conversation.timestamp) && (
                          <Box
                            className="conversation-unread-dot"
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.secondary.main,
                              transition: 'opacity 0.15s ease',
                              // Hidden while this row's menu is open - same portal-hover
                              // quirk as the kebab below.
                              opacity:
                                menuState?.conversation.timestamp ===
                                conversation.timestamp
                                  ? 0
                                  : 1,
                            }}
                          />
                        )}
                        <IconButton
                          data-ph-capture-attribute-element="moviefone-conversation-menu"
                          size="small"
                          className="conversation-delete-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenuState({
                              anchor: event.currentTarget,
                              conversation,
                            });
                          }}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            // Stays visible (rather than fading with the row's hover state)
                            // while its own menu is open - the menu is a portal, so opening
                            // it moves the mouse off the row as far as :hover is concerned.
                            opacity:
                              menuState?.conversation.timestamp ===
                              conversation.timestamp
                                ? 1
                                : 0,
                            color: theme.palette.text.secondary,
                            transition: 'opacity 0.15s ease',
                          }}
                        >
                          <MoreVertIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Stack>
                  </motion.div>
                ))}
            </AnimatePresence>
          </Stack>
        ) : (
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                width: '1px',
                transform: 'translateX(-50%)',
                backgroundColor: theme.palette.gray,
              }}
            />
            <IconButton
              data-ph-capture-attribute-element="moviefone-threads-toggle"
              size="small"
              onClick={() => setOpen(!open)}
              sx={{
                position: 'relative',
                color: theme.palette.sgGreen,
                backgroundColor: theme.palette.background.default,
                '&:hover': {
                  backgroundColor: theme.palette.background.default,
                },
              }}
            >
              <ForumOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Stack>
      <Menu
        anchorEl={menuState?.anchor}
        open={menuState != null}
        onClose={() => setMenuState(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuState != null) {
              setConversationPendingDelete(menuState.conversation);
            }
            setMenuState(null);
          }}
          sx={{ fontSize: '13px', gap: 1 }}
        >
          <DeleteIcon fontSize="small" />
          Delete
        </MenuItem>
      </Menu>
      <Dialog
        open={conversationPendingDelete != null}
        onClose={() => setConversationPendingDelete(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          Are you sure you want to delete
          {conversationPendingDelete != null
            ? ` "${conversationPendingDelete.title}"`
            : ' this conversation'}
          ? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConversationPendingDelete(null)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (conversationPendingDelete != null) {
                onDeleteConversation(conversationPendingDelete.timestamp);
              }
              setConversationPendingDelete(null);
            }}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default MoviefonePreviousThreads;
